import {
  formatDistanceStrict,
  isSameDay,
  isLeapYear,
  format,
  parseISO,
} from 'date-fns';

import {
  swKE,
  soSO,
  bsBA,
  xhZA,
  azAZ,
  amET,
  hiIN,
  neNP,
} from './custom-locale';

import {
  af,
  arSA,
  bn,
  bg,
  cs,
  da,
  el,
  enGB,
  enCA,
  enUS,
  es,
  et,
  fi,
  fr,
  de,
  he,
  hr,
  hu,
  id,
  ja,
  ko,
  lv,
  lt,
  mn,
  ms,
  nb,
  nl,
  it,
  ptBR,
  pl,
  pt,
  ro,
  ru,
  sk,
  sl,
  sr,
  sv,
  ta,
  th,
  tr,
  vi,
  uk,
  zhCN,
  zhTW,
} from 'date-fns/locale';
import { TimezoneNames } from './types/dates.type';

export const locales: { [key: string]: Locale } = {
  'af-ZA': af,
  'az-AZ': azAZ,
  'ar-001': arSA,
  'am-ET': amET,
  'bn-BD': bn,
  'bs-BA': bsBA,
  'bg-BG': bg,
  'cs-CZ': cs,
  'da-DK': da,
  'el-GR': el,
  'en-GB': enGB,
  'en-AU': enGB,
  'en-CA': enCA,
  'en-US': enUS,
  'et-EE': et,
  'fi-FI': fi,
  'fr-CA': fr,
  'fr-FR': fr,
  'es-US': es,
  'es-419': es,
  'de-DE': de,
  'he-IL': he,
  'hi-IN': hiIN,
  'hr-HR': hr,
  'hu-HU': hu,
  'id-ID': id,
  'ja-JP': ja,
  'ko-KR': ko,
  'lv-LV': lv,
  'lt-LT': lt,
  'mn-MN': mn,
  'ms-MY': ms,
  'nb-NO': nb,
  'ne-NP': neNP,
  'nl-NL': nl,
  'es-ES': es,
  'it-IT': it,
  'pt-BR': ptBR,
  'pl-PL': pl,
  'pt-PT': pt,
  'ro-RO': ro,
  'ru-RU': ru,
  'sk-SK': sk,
  'sr-RS': sr,
  'sv-SE': sv,
  'sl-SI': sl,
  'so-SO': soSO,
  'sw-KE': swKE,
  'ta-IN': ta,
  'th-TH': th,
  'tl-PH': enUS, // TODO add custom locale because 'fil' locale support has been removed from date-fns https://workivate.atlassian.net/browse/WB-13109
  'tr-TR': tr,
  'uk-UA': uk,
  'ur-PK': enUS, // TODO add custom locale because Urdu is not supported by date-fns
  // https://workivate.atlassian.net/browse/WB-13659
  'vi-VN': vi,
  'xh-ZA': xhZA,
  'zu-ZA': enUS, // TODO add custom locale because isiZulu is not supported by date-fns
  'zh-CN': zhCN,
  'zh-HK': zhTW,
};

const DEFAULT_LOCALE_FOR_DATES = 'en-GB'; // Hermes and Zeus have DEFAULT_LOCALE = 'en_GB' but date-fns requires the hyphen

export function getRelativeTime(timestamp, locale) {
  const date = timestamp ? 1000 * timestamp : new Date();

  return formatDistanceStrict(date, new Date(), {
    addSuffix: true,
    locale: locales[locale.replace('_', '-')],
  });
}

export const isToday = date => isSameDay(new Date(date), new Date());

export const hasInvalidNbrOfDays = (dayValue, monthValue) =>
  [3, 5, 8, 10].includes(monthValue) && dayValue > 30;

const today = new Date();
today.setHours(0, 0, 0, 0);
const currentYear = today.getFullYear();

export const isAFutureDate = dateValue => today < dateValue;

export const isAFutureConcatFormatDate = concatFormatDateValue => {
  const formattedToday = toConcatFormat(today) || 0;
  // in practice today should always be valid but this keeps Typescript happy
  return formattedToday < concatFormatDateValue;
};

export const yearIsOutOfExpectedRange = yearValue =>
  yearValue > currentYear || yearValue < 1900 || isNaN(yearValue);

export const isAnInvalidFebruaryDay = (dayValue, monthValue, yearValue) =>
  monthValue === 1 &&
  (dayValue > 29 ||
    (dayValue === 29 && !isLeapYear(new Date(yearValue, 0, 1))));

export function toConcatFormat(date) {
  if (!date) return null;

  const concatFormatDate = format(parseISO(date.toISOString()), 'yyyyMMdd');

  return parseInt(concatFormatDate);
}

export const getPossibleDaysInMonth = (month, year) => {
  let value = 31;

  if ([3, 5, 8, 10].includes(+month)) {
    value = 30;
  }

  if (+month === 1) {
    value = isLeapYear(new Date(year, 0, 1)) ? 29 : 28;
  }

  return value;
};

export const escapeRelativeLocaleString = str => str.replace(/'/g, "''");

export function toDate(concatDate, format = 'YYYY-MM-DD') {
  const num = (concatDate || '').toString();

  if (num.length < 8) return null;

  const dateString = format
    .replace('YYYY', num.substring(0, 4))
    .replace('MM', num.substring(4, 6))
    .replace('DD', num.substring(6, 8));

  return new Date(dateString);
}

export const normalizeUTC = (utcDate: string) => {
  return utcDate.includes('Z') ? utcDate : `${utcDate}Z`;
};

export const getFormattedDate = (
  date: undefined | string | number | Date,
  formatString = 'yyyy/MM/dd',
  options: object = {},
  locale: string = DEFAULT_LOCALE_FOR_DATES,
): string => {
  const formatOptions = {
    locale: locales[locale],
    ...options,
  };

  if (formatString === '') {
    formatString = 'yyyy/MM/dd';
  }

  if (!date) {
    return format(new Date(), formatString, formatOptions);
  }

  const normDate =
    typeof date === 'string' ? parseISO(normalizeUTC(date)) : date;

  return format(normDate, formatString, formatOptions);
};

/**
 * As of Sept 12th 2019 we receive dates from the Serverless team as 2019-09-12,
 * because JS wants to follow ISO 8601 standard and they (Serverless) don't give
 * us a timezone, JS will automatically deduct a day if the user is in a - GMT
 * timezone. This means we have to make an assumption that the date they send us is
 * as expected and we need to force the date through mathematically deducting the timezone offset.
 *
 * @param date {Date | string} - the date we will be formatting
 * @param dateFormat {string} [dd MMM yyyy] - the date format relative to Date-fns' documentation
 * @returns {string} - the front-end displayable date
 *
 * @example
 * getFormattedDateWithTimeZoneOffset('2019-09-09') => 09 Sept 2019
 *
 * @example
 * getFormattedDateWithTimeZoneOffset(Thu Sep 12 2019 12:21:47 GMT+0100 (BST)) => 09 Sept 2019
 *
 * @example
 * getFormattedDateWithTimeZoneOffset(Thu Sep 12 2019 12:21:47 GMT-0500 (EDT), yyyy-MM-dd) => 2019-09-09
 */

export const getFormattedDateWithTimeZoneOffset = (
  date: Date | string,
  dateFormat = 'dd MMM yyyy',
  locale = DEFAULT_LOCALE_FOR_DATES,
): string => {
  const jsDateWithoutTimeZone = getDateWithTimeZoneOffset(date);

  const formatOptions = {
    locale: locales[locale],
  };

  return format(jsDateWithoutTimeZone, dateFormat, formatOptions);
};

export const getDateWithTimeZoneOffset = (date: Date | string) => {
  const jsDate = new Date(date);
  return new Date(jsDate.getTime() - jsDate.getTimezoneOffset() * -60000);
};

/**
 * This function is to compute the correct date and time according to a specific time zone
 * typically the company's time zone instead of the user's local time zone
 * It can handle both short dates (e.g. 2024-08-25) and long dates (e.g. 2024-08-25T00:00:00.000Z)
 * We first convert the time with Intl to the target time zone
 * then we offset the user's local time zone to make sure the date is correct
 *
 * @param {string} date string - The short-date or long-date to convert.
 * @param {string} timezone - The target timezone (e.g.'America/New_York').
 * @param {string} outputFormat - The desired output format (e.g. 'yyyy-MM-dd').
 * @returns {string} - The formatted date string in the target timezone.
 * @example
 * Time zone: America/New_York (UTC-4)
 * getFormattedDateWithTimeZone('2024-08-25') => 25 Aug 2024
 * getFormattedDateWithTimeZone('2024-08-25T00:00:00.000Z') => 24 Aug 2024
 * Time zone : Australia/Melbourne (UTC+10)
 * getFormattedDateWithTimeZone('2024-08-25') => 25 Aug 2024
 * getFormattedDateWithTimeZone('2024-08-24T14:00:00.000Z') => 25 Aug 2024
 */
export function getFormattedDateWithTimeZone(
  date: string | Date | null | undefined,
  timezone: string,
  outputFormat = 'dd MMM yyyy',
  locale = DEFAULT_LOCALE_FOR_DATES,
): string {
  if (!date) return '';

  const shortDateRegex = /^\d{4}-\d{2}-\d{2}$/;

  // for short dates we only need to offset user's local timezone
  if (typeof date === 'string' && shortDateRegex.test(date)) {
    return getFormattedDateWithTimeZoneOffset(date, outputFormat, locale);
  }

  const formatOptions = {
    locale: locales[locale],
  };

  // get the date in the correct time zone
  const offsetDate = getDateWithTimeZone(date, timezone, locale);

  return format(offsetDate, outputFormat, formatOptions);
}

export const getDateWithTimeZone = (
  date: string | Date,
  timezone: string,
  locale = DEFAULT_LOCALE_FOR_DATES,
) => {
  const shortDateRegex = /^\d{4}-\d{2}-\d{2}$/;

  // for short dates we only need to offset user's local timezone
  if (typeof date === 'string' && shortDateRegex.test(date)) {
    return getDateWithTimeZoneOffset(date);
  }

  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false, //24-hour time for constructing UTC date
  };

  const formatter = new Intl.DateTimeFormat(locale, options);
  const parts = formatter.formatToParts(new Date(date));

  const year = parts.find(part => part.type === 'year')?.value ?? '';
  const month = parts.find(part => part.type === 'month')?.value ?? '';
  const day = parts.find(part => part.type === 'day')?.value ?? '';
  const hour = parts.find(part => part.type === 'hour')?.value ?? '';
  const minute = parts.find(part => part.type === 'minute')?.value ?? '';
  const second = parts.find(part => part.type === 'second')?.value ?? '';

  // date string in correct time zone
  const UTCDate = `${year}-${month}-${day}T${hour}:${minute}:${second}Z`;

  // subtract the user's local timezone offset to get the correct date (JS Date object)
  const offsetDate = getDateWithTimeZoneOffset(UTCDate);

  return offsetDate;
};

export const formatTimeZone = (
  date: string | Date,
  timezone?: string | null,
  format: TimezoneNames = 'short',
  locale = DEFAULT_LOCALE_FOR_DATES,
) => {
  if (!timezone || !date) return '';

  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    timeZoneName: format,
  });

  const parts = formatter.formatToParts(getDateWithTimeZone(date, timezone));
  const timeZoneName =
    parts.find(part => part.type === 'timeZoneName')?.value ?? '';

  return timeZoneName;
};
